import { useState, useEffect } from 'react';

/**
 * useMediaQuery Hook
 * Detects if a CSS media query matches the current viewport
 * WCAG 2.2 compliant - handles dynamic sizing changes
 *
 * @param query - CSS media query string (e.g., '(max-width: 700px)')
 * @returns boolean - true if the query matches
 */
const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    // Check if window is available (SSR safety)
    if (typeof window === 'undefined') return;

    const mediaQueryList = window.matchMedia(query);

    const listener = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Initial check
    setMatches(mediaQueryList.matches);

    // Listen for changes (WCAG 2.2 compliant way of handling dynamic sizing)
    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', listener);
    } else {
      // Fallback for older browsers
      mediaQueryList.addListener(listener);
    }

    return () => {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener('change', listener);
      } else {
        mediaQueryList.removeListener(listener);
      }
    };
  }, [query]);

  return matches;
};

// Common breakpoint constants
export const MOBILE_BREAKPOINT = '(max-width: 700px)';
export const TABLET_BREAKPOINT = '(max-width: 1024px)';

export default useMediaQuery;
